/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { PageTitle } from '../../../_metronic/layout/core'
import axios from 'axios'
import { Link } from 'react-router-dom'
import { useLoader } from '../../context/LoaderContext'
import ReactApexChart from 'react-apexcharts'
import { validateDateRange } from '../../../_metronic/helpers/dateRange'

/* ─────────────────────── constants ─────────────────────── */

const RANGE_OPTIONS = [
  { value: '30d', label: 'Last 30 Days' },
  { value: '3mon', label: '3 Months' },
  { value: '6mon', label: '6 Months' },
  { value: '12mon', label: '12 Months' },
  { value: 'custom', label: 'Custom' },
]

const STATUS_COLOR_MAP = {
  INCOMPLETE: '#FFC700',
  'AGENT ASSIGNED': '#009EF7',
  CANCELED: '#F1416C',
  PENDING: '#7239EA',
  COMPLETED: '#50CD89',
  CONFIRMED: '#00A3FF',
  'PROVIDER CANCELLED': '#FF8F8F',
  'SERVICE IN PROGRESS': '#20D489',
}

/* ─────────────────────── sub-components ─────────────────── */

const StatCard = ({ title, value, color, to }) => (
  <div className='col-xl-2 col-md-4 col-6 mb-5'>
    <div
      className={`card border-0 shadow-sm h-100 border-start border-4 border-${color}`}
    >
      <div className='card-body p-5'>
        <div className='text-muted fw-semibold fs-7 mb-2'>{title}</div>
        <Link to={to} className={`text-${color} fw-bolder fs-2x d-block lh-1`}>
          {value != null ? value.toLocaleString() : '—'}
        </Link>
      </div>
    </div>
  </div>
)

const PeriodStatCard = ({ label, value, color }) => (
  <div className='col-md-3 col-sm-6 mb-5'>
    <div className='card border-0 shadow-sm h-100'>
      <div className='card-body p-5 text-center'>
        <div className='text-muted fw-semibold fs-7 mb-2'>{label}</div>
        <div className='fw-bolder fs-2x' style={{ color }}>
          {value != null ? value.toLocaleString() : '—'}
        </div>
      </div>
    </div>
  </div>
)

/* ─────────────────────── chart helpers ──────────────────── */

const sharedChartBase = {
  toolbar: { show: false },
  fontFamily: 'inherit',
  animations: { enabled: true, speed: 400 },
}

function buildAreaOptions(dates, colors, yTitle) {
  return {
    chart: { ...sharedChartBase, type: 'area' },
    colors,
    stroke: { curve: 'smooth', width: 2 },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.02,
        stops: [0, 100],
      },
    },
    xaxis: {
      categories: dates,
      tickAmount: Math.min(dates.length, 10),
      labels: {
        rotate: -30,
        style: { fontSize: '11px' },
        formatter: (val) => {
          if (!val) return ''
          const d = new Date(val)
          return isNaN(d)
            ? val
            : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
        },
      },
    },
    yaxis: {
      title: { text: yTitle, style: { fontSize: '12px' } },
      min: 0,
      forceNiceScale: true,
    },
    tooltip: {
      x: {
        formatter: (val) => {
          // ApexCharts passes a 1-based index for category xaxis, not the raw value
          const dateStr = dates[val - 1]
          if (!dateStr) return String(val)
          const d = new Date(dateStr)
          return isNaN(d)
            ? dateStr
            : d.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
        },
      },
    },
    legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px' },
    dataLabels: { enabled: false },
    grid: { borderColor: '#f1f1f1', strokeDashArray: 4 },
  }
}

function buildDonutOptions(statusMix) {
  return {
    chart: { ...sharedChartBase, type: 'donut' },
    labels: statusMix.map((s) => s.status),
    colors: statusMix.map((s) => STATUS_COLOR_MAP[s.status] || '#808080'),
    legend: { position: 'bottom', fontSize: '11px' },
    dataLabels: { enabled: true, style: { fontSize: '11px' } },
    plotOptions: {
      pie: {
        donut: {
          size: '62%',
          labels: {
            show: true,
            total: { show: true, label: 'Total', fontSize: '13px' },
          },
        },
      },
    },
  }
}

function buildBarOptions(labels) {
  return {
    chart: { ...sharedChartBase, type: 'bar' },
    colors: ['#009EF7'],
    plotOptions: { bar: { borderRadius: 5, columnWidth: '50%' } },
    xaxis: { categories: labels },
    yaxis: { min: 0, forceNiceScale: true },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    grid: { borderColor: '#f1f1f1', strokeDashArray: 4 },
  }
}

/* ─────────────────────── main component ────────────────── */

const DashboardWrapper = () => {
  const intl = useIntl()
  const { showLoader, closeLoader } = useLoader()

  const [stats, setStats] = useState(null)
  const [range, setRange] = useState('30d')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const customDateError = validateDateRange(customFrom, customTo)

  const fetchStats = useCallback(
    (rangeVal, from, to) => {
      const token = localStorage.getItem('kt-auth-react-v.api_token')
      const params = { range: rangeVal }
      if (rangeVal === 'custom') {
        if (!from || !to) return
        params.from = from
        params.to = to
      }
      showLoader()
      axios
        .get(`${process.env.REACT_APP_API_URL}/admin/dashboard-stats`, {
          headers: { Authorization: ` ${token}` },
          params,
        })
        .then((res) => setStats(res.data.result))
        .catch((err) => console.log(err))
        .finally(() => closeLoader())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    fetchStats('30d')
  }, [fetchStats])

  const handleRangeChange = (val) => {
    setRange(val)
    if (val !== 'custom') fetchStats(val)
  }

  const handleCustomApply = () => {
    if (customDateError) return
    if (customFrom && customTo) fetchStats('custom', customFrom, customTo)
  }

  /* ── derived data ── */
  const bookingSeries = stats?.BOOKINGS?.series || []
  const userSeries = stats?.USERS?.series || []
  const statusMix = stats?.BOOKINGS?.statusMix || []
  const sourceTotals = stats?.BOOKINGS?.sourceTotals || {}
  const window = stats?.WINDOW

  const bookingDates = bookingSeries.map((s) => s.date)
  const userDates = userSeries.map((s) => s.date)

  const bookingsAreaOptions = buildAreaOptions(
    bookingDates,
    ['#009EF7', '#50CD89', '#F1416C'],
    'Bookings',
  )
  const bookingsAreaSeries = [
    { name: 'Website', data: bookingSeries.map((s) => s.sources.website) },
    { name: 'Portal', data: bookingSeries.map((s) => s.sources.portal) },
    { name: 'App', data: bookingSeries.map((s) => s.sources.app) },
  ]

  const usersAreaOptions = buildAreaOptions(userDates, ['#7239EA'], 'New Users')
  const usersAreaSeries = [
    { name: 'New Users', data: userSeries.map((s) => s.total) },
  ]

  const donutOptions = buildDonutOptions(statusMix)
  const donutSeries = statusMix.map((s) => s.count)

  const sourceLabels = Object.keys(sourceTotals).map(
    (k) => k.charAt(0).toUpperCase() + k.slice(1),
  )
  const barOptions = buildBarOptions(sourceLabels)
  const barSeries = [{ name: 'Bookings', data: Object.values(sourceTotals) }]

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'MENU.DASHBOARD' })}
      </PageTitle>

      {/* ── Global Stats ── */}
      <div className='row g-4 mb-6'>
        <StatCard
          title='Total Users'
          value={stats?.totalUsers}
          color='primary'
          to='/manage-user/consumer'
        />
        <StatCard
          title='Total Providers'
          value={stats?.totalProviders}
          color='dark'
          to='/manage-user/provider'
        />
        <StatCard
          title='Total Bookings'
          value={stats?.totalBookings}
          color='success'
          to='/booking'
        />
        <StatCard
          title='Total Services'
          value={stats?.totalServices}
          color='info'
          to='/services'
        />
        <StatCard
          title='Total Categories'
          value={stats?.totalCategories}
          color='warning'
          to='/services'
        />
        <StatCard
          title='Total Packages'
          value={stats?.totalProducts}
          color='danger'
          to='/products'
        />
      </div>

      {/* ── Range Selector ── */}
      <div className='card shadow-sm mb-6'>
        <div className='card-body py-4 d-flex flex-wrap align-items-center gap-3'>
          <span className='fw-bold text-gray-700 me-1'>Period:</span>
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`btn btn-sm ${
                range === opt.value ? 'btn-primary' : 'btn-light-primary'
              }`}
              onClick={() => handleRangeChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}

          {range === 'custom' && (
            <div className='d-flex align-items-center gap-2 ms-2'>
              <input
                type='date'
                className='form-control form-control-sm w-auto'
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <span className='text-muted small'>to</span>
              <input
                type='date'
                className='form-control form-control-sm w-auto'
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
              />
              <button
                className='btn btn-sm btn-primary'
                onClick={handleCustomApply}
                disabled={!customFrom || !customTo || !!customDateError}
              >
                Apply
              </button>
              {customDateError && (
                <span className='text-danger small'>{customDateError}</span>
              )}
            </div>
          )}

          {window && (
            <span className='ms-auto badge badge-light-primary fs-7 fw-semibold'>
              {window.label}&nbsp;·&nbsp;{window.from} → {window.to}
            </span>
          )}
        </div>
      </div>

      {/* ── Period Summary Cards ── */}
      {stats?.BOOKINGS && (
        <div className='row g-4 mb-6'>
          <PeriodStatCard
            label='Bookings in Period'
            value={stats.BOOKINGS.total}
            color='#009EF7'
          />
          <PeriodStatCard
            label='App Bookings'
            value={stats.BOOKINGS.sourceTotals?.app ?? 0}
            color='#7239EA'
          />
          <PeriodStatCard
            label='Website Bookings'
            value={stats.BOOKINGS.sourceTotals?.website ?? 0}
            color='#50CD89'
          />
          <PeriodStatCard
            label='Portal Bookings'
            value={stats.BOOKINGS.sourceTotals?.portal ?? 0}
            color='#F1416C'
          />
        </div>
      )}

      {/* ── Charts Row 1: Bookings Trend + Status Donut ── */}
      <div className='row g-6 mb-6'>
        <div className='col-xl-8'>
          <div className='card shadow-sm h-100'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title fw-bolder fs-4'>Bookings Trend</h3>
              <div className='card-toolbar'>
                <span className='badge badge-light-primary'>by source</span>
              </div>
            </div>
            <div className='card-body pt-2'>
              {bookingSeries.length > 0 ? (
                <ReactApexChart
                  options={bookingsAreaOptions}
                  series={bookingsAreaSeries}
                  type='area'
                  height={300}
                />
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{ height: 300 }}
                >
                  <span className='text-muted'>No data available</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='col-xl-4'>
          <div className='card shadow-sm h-100'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title fw-bolder fs-4'>Booking Status Mix</h3>
            </div>
            <div className='card-body d-flex align-items-center justify-content-center pt-0'>
              {donutSeries.length > 0 ? (
                <ReactApexChart
                  options={donutOptions}
                  series={donutSeries}
                  type='donut'
                  height={320}
                  width='100%'
                />
              ) : (
                <span className='text-muted'>No data available</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Charts Row 2: Users Trend + Source Bar ── */}
      <div className='row g-6 mb-6'>
        <div className='col-xl-8'>
          <div className='card shadow-sm h-100'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title fw-bolder fs-4'>
                New User Registrations
              </h3>
            </div>
            <div className='card-body pt-2'>
              {userSeries.length > 0 ? (
                <ReactApexChart
                  options={usersAreaOptions}
                  series={usersAreaSeries}
                  type='area'
                  height={260}
                />
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{ height: 260 }}
                >
                  <span className='text-muted'>No data available</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='col-xl-4'>
          <div className='card shadow-sm h-100'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title fw-bolder fs-4'>Bookings by Source</h3>
            </div>
            <div className='card-body pt-2'>
              {Object.keys(sourceTotals).length > 0 ? (
                <ReactApexChart
                  options={barOptions}
                  series={barSeries}
                  type='bar'
                  height={260}
                />
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{ height: 260 }}
                >
                  <span className='text-muted'>No data available</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export { DashboardWrapper }
